// Derive an ed25519 keypair from the user's BIP-39 mnemonic for /admin
// sign-on. Keeps the chat identity and the admin identity in one place:
// no second password, no invite redeem step.
//
// The derivation is intentionally NOT compatible with Evolu's internal
// SLIP-21 owner-key derivation. We HKDF-expand the raw mnemonic with a
// domain-separation `info` string so the resulting key only signs
// admin-login challenges — even if Evolu ever exposes its own seed,
// these keys can't be reused for sync auth.

import { ed25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";

const INFO = "jednadvacet-admin-login-v1";

function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i].toString(16).padStart(2, "0");
  }
  return s;
}

function b64urlToBytes(b64url: string): Uint8Array {
  const padded = b64url.replace(/-/g, "+").replace(/_/g, "/")
    + "=".repeat((4 - b64url.length % 4) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

/** Derive a 32-byte ed25519 secret from a 24-word BIP-39 mnemonic.
 *  Pure HKDF, no PBKDF2 (the mnemonic itself is already 256 bits of
 *  entropy, no stretching needed against brute force given the password
 *  threat model — server never sees the mnemonic). */
function deriveSecret(mnemonic: string): Uint8Array {
  const ikm = utf8(mnemonic.trim().normalize("NFKD"));
  // HKDF-SHA-256 → 32 bytes. salt is empty (mnemonic is the only secret).
  return hkdf(sha256, ikm, undefined, utf8(INFO), 32);
}

export interface OwnerKeypair {
  /** 64-hex (32 bytes) — share with backend at apply-time. */
  pubkeyHex: string;
  /** Sign a server-issued challenge nonce (base64url). Returns 128-hex
   *  (64-byte ed25519 signature). */
  sign(nonceB64url: string): string;
}

export function deriveOwnerKeypair(mnemonic: string): OwnerKeypair {
  const secret = deriveSecret(mnemonic);
  const pubkey = ed25519.getPublicKey(secret);
  return {
    pubkeyHex: bytesToHex(pubkey),
    sign(nonceB64url: string): string {
      const msg = b64urlToBytes(nonceB64url);
      const sig = ed25519.sign(msg, secret);
      return bytesToHex(sig);
    },
  };
}
